import { NavLink, generatePath, matchPath } from 'react-router-dom';
import APP_PATHS from '../../constants/app-paths';
import APP_ROUTES from '../../constants/app-routes';
import CAPABILITIES from '../../constants/capabilities.json';
import GRADE_IDS from '../../constants/grade-ids.json';
import { LIST_TYPES } from '@llt-frontend/react-material-list';
import STUDENT_FEATURES from '../../constants/student-features.json';
import { filterByGradeIds } from '../../utilities/subjects/filter';
import getItemData from './get-item-data';
import getSubjectNavigationSection from '../../utilities/side-navigation/get-subject-section';
import mapAssignmentsToNavListItems from './map-assignments-to-nav-list-items';

// eslint-disable-next-line complexity
export default function getNavList({
  activityLabsSubjects,
  assignments,
  clubNavSection,
  labUser,
  onDemand: { coursesLoading: onDemandCoursesLoading, courses: onDemandCourses },
  selfStudyNavSection,
  translate,
}) {
  const { activeStudent, hasCapability } = labUser;

  const {
    loading: studentSubjectsLoading,
    error: failedToLoadStudentSubjects,
    data: studentSubjects,
  } = activityLabsSubjects;

  const hasSubjectManagementCapability = hasCapability(CAPABILITIES.subjectManagement);
  const selfStudySubjects = filterByGradeIds(studentSubjects || [], {
    ids: [GRADE_IDS.camps, GRADE_IDS.clubs],
    inclusive: false,
  });

  const homeworkHelpLinks = [];

  if (activeStudent?.hasEnablement(STUDENT_FEATURES.essayEditing)) {
    homeworkHelpLinks.push(
      getItemData({
        exact: false,
        graphic: 'auto_stories',
        primaryText: translate('essayReview'),
        to: APP_PATHS.studySupportEssays,
      })
    );
  }

  const mainNavigationItems = [
    getItemData({
      graphic: 'home',
      primaryText: translate('home'),
      to: APP_PATHS.home,
    }),
    getItemData({
      graphic: 'ondemand_video',
      primaryText: translate('sessionRecaps'),
      to: APP_PATHS.sessionRecaps,
      exact: false,
    }),
  ];

  if (hasCapability(CAPABILITIES.agendaAccess)) {
    mainNavigationItems.push(
      getItemData({
        graphic: 'today',
        primaryText: translate('studentAgenda'),
        to: APP_PATHS.studentAgenda,
      })
    );
  }

  const navLists = [
    {
      item: {
        'aria-label': translate('mainNavigationLabel'),
        items: mainNavigationItems,
        listType: LIST_TYPES.navigation,
        withDivider: true,
      },
      include: true,
    },
    {
      item: {
        'aria-label': translate('homeworkHelpNavigationLabel'),
        heading: translate('homeworkHelp'),
        items: homeworkHelpLinks,
        listType: LIST_TYPES.navigation,
        withDivider: true,
      },
      include: homeworkHelpLinks.length,
    },
    {
      item: {
        'aria-label': translate('onDemandNavigationLabel'),
        heading: 'On-Demand Courses',
        items: onDemandCourses?.map((course) => {
          return getItemData({
            graphic: course.pageIcon || 'school',
            primaryText: course.name,
            to: generatePath(APP_ROUTES.onDemandCourse, { slug: course.slug }),
          });
        }),
        listType: LIST_TYPES.navigation,
        withDivider: true,
      },
      include: !onDemandCoursesLoading && onDemandCourses?.length,
    },
    {
      item: {
        'aria-label': translate('assessmentsNavigationLabel'),
        heading: translate('assessments'),
        items: [
          getItemData({
            isActive: (match, { pathname }) => {
              return match && !matchPath(pathname, '/assessments/add/:gradeId?');
            },
            exact: false,
            graphic: 'assignment',
            primaryText: translate('assessments'),
            to: '/assessments',
          }),
          {
            activeClassName: 'mdc-deprecated-list-item--selected',
            className: 'mdc-deprecated-list-item--add',
            component: NavLink,
            exact: false,
            graphic: 'add_circle_outline',
            primaryText: 'Add Assessment',
            to: APP_PATHS.addAssessment,
          },
        ],
        listType: LIST_TYPES.navigation,
        withDivider: true,
      },
      include: true,
    },
    {
      item: getSubjectNavigationSection({
        ...clubNavSection,
        actions: ['add', 'delete', 'edit'],
        addSubjectLink: APP_PATHS.addActivityLab,
        addSubjectText: translate('addActivityLab'),
        editToggleLabel: translate('editSubjects'),
        errorLoadingText: translate('clubLoadError'),
        failedToLoadSubjects: failedToLoadStudentSubjects,
        heading: translate('activityLabs'),
        loadingText: translate('loadingActivityLabs'),
        navRoot: APP_PATHS.activityLabs,
        subjectType: 'activity-labs',
        subjectsLoading: studentSubjectsLoading,
        subjects: filterByGradeIds(studentSubjects || [], {
          ids: [105, 106],
        }),
      }),
      include: hasCapability(CAPABILITIES.activityLabAccess),
    },
    {
      item: {
        'aria-label': translate('assignmentsNavigationLabel'),
        heading: translate('assignments'),
        items: mapAssignmentsToNavListItems(assignments),
        listType: LIST_TYPES.navigation,
        withDivider: true,
      },
      include: assignments?.length,
    },
    {
      item: getSubjectNavigationSection({
        ...selfStudyNavSection,
        actions: hasSubjectManagementCapability ? ['add', 'delete', 'edit', 'reassign'] : [],
        addSubjectLink: '/subjects/add',
        addSubjectText: translate('addSubject'),
        editToggleLabel: translate('editSubjects'),
        errorLoadingText: translate('subjectLoadError'),
        failedToLoadSubjects: failedToLoadStudentSubjects,
        heading: translate('selfStudy'),
        loadingText: translate('loadingSubjects'),
        navRoot: '/subjects',
        subjectType: 'self-study',
        subjectsLoading: studentSubjectsLoading,

        subjects: selfStudySubjects,
      }),
      include: selfStudySubjects.length || hasSubjectManagementCapability,
    },
  ];

  return navLists.reduce((res, { item, include }) => {
    include && res.push(item);
    return res;
  }, []);
}
